import { PageContent } from '@/components/shared/page-content'
import { HeaderSection } from '@/components/shared/header-section'
import { Card } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useState, useEffect } from 'react'
import { toast } from 'sonner'
import { useMutation, useQuery } from '@apollo/client'
import {
  GET_SHOP_SETTINGS,
  UPDATE_SHOP_SETTINGS_V2,
} from './languageSettingsQueries'
import {
  AVAILABLE_LANGUAGES,
  createDefaultEnabledLanguage,
  type EnabledLanguage,
} from './languageConstants'

export default function LanguageSettings() {
  const { data, loading, refetch } = useQuery(GET_SHOP_SETTINGS)
  const [updateShopSettings, { loading: isSaving }] = useMutation(
    UPDATE_SHOP_SETTINGS_V2
  )

  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([])
  const [originalSettings, setOriginalSettings] = useState<any>(null)
  const [languageConfigs, setLanguageConfigs] = useState<
    Record<string, EnabledLanguage>
  >({})
  const [selectedCurrency, setSelectedCurrency] = useState<string>('EUR')

  // Available currencies
  const AVAILABLE_CURRENCIES = [
    { code: 'USD', name: 'USD ($)', symbol: '$' },
    { code: 'EUR', name: 'EUR (€)', symbol: '€' },
    { code: 'PLN', name: 'PLN (zł)', symbol: 'zł' },
  ]

  // Initialize selected languages when data is loaded
  useEffect(() => {
    if (data?.findFirstShopSetting) {
      const enabledLanguageCodes =
        data.findFirstShopSetting.enabled_languages?.map(
          (lang: EnabledLanguage) => lang.language
        ) || []
      setSelectedLanguages(enabledLanguageCodes)
      setOriginalSettings(data.findFirstShopSetting)
      setSelectedCurrency(data.findFirstShopSetting.currency || 'EUR')

      // Initialize language configs
      const configs: Record<string, EnabledLanguage> = {}
      data.findFirstShopSetting.enabled_languages?.forEach(
        (lang: EnabledLanguage) => {
          configs[lang.language] = lang
        }
      )
      setLanguageConfigs(configs)
    }
  }, [data])

  // Update language configs when selected languages change
  useEffect(() => {
    const newConfigs = { ...languageConfigs }

    // Add configs for newly selected languages
    selectedLanguages.forEach(langCode => {
      if (!newConfigs[langCode]) {
        const existingLang = originalSettings?.enabled_languages?.find(
          (lang: EnabledLanguage) => lang.language === langCode
        )
        newConfigs[langCode] =
          existingLang || createDefaultEnabledLanguage(langCode)
      }
    })

    // Remove configs for deselected languages
    Object.keys(newConfigs).forEach(langCode => {
      if (!selectedLanguages.includes(langCode)) {
        delete newConfigs[langCode]
      }
    })

    setLanguageConfigs(newConfigs)
  }, [selectedLanguages, originalSettings])

  const handleLanguageToggle = (languageCode: string, checked: boolean) => {
    if (checked) {
      setSelectedLanguages(prev => [...prev, languageCode])
    } else {
      setSelectedLanguages(prev => prev.filter(code => code !== languageCode))
    }
  }

  const updateLanguageConfig = (
    languageCode: string,
    updates: Partial<EnabledLanguage>
  ) => {
    setLanguageConfigs(prev => {
      const updatedConfig = {
        ...prev[languageCode],
        ...updates,
      }

      // If web_address_enabled is being set to false, also set default_language to false
      if (updates.web_address_enabled === false) {
        updatedConfig.default_language = false
      }

      return {
        ...prev,
        [languageCode]: updatedConfig,
      }
    })
  }

  const handleDefaultLanguageChange = (
    languageCode: string,
    isDefault: boolean
  ) => {
    if (isDefault) {
      // Set this language as default and unset all others
      const updatedConfigs = { ...languageConfigs }
      Object.keys(updatedConfigs).forEach(code => {
        updatedConfigs[code] = {
          ...updatedConfigs[code],
          default_language: code === languageCode,
        }
      })
      setLanguageConfigs(updatedConfigs)
    } else {
      // Don't allow unsetting default if it's the only language
      if (selectedLanguages.length === 1) {
        toast.error('At least one language must be set as default')
        return
      }
      updateLanguageConfig(languageCode, { default_language: false })
    }
  }

  const validateConfigurations = () => {
    const defaultLanguages = Object.values(languageConfigs).filter(
      config => config.default_language
    )
    if (defaultLanguages.length === 0 && selectedLanguages.length > 0) {
      toast.error('At least one language must be set as default')
      return false
    }

    // Validate web addresses for languages with domain enabled
    for (const config of Object.values(languageConfigs)) {
      if (config.web_address_enabled && !config.web_address.trim()) {
        const language = AVAILABLE_LANGUAGES.find(
          l => l.code === config.language
        )
        toast.error(
          `Website address is required for ${language?.name || config.language}`
        )
        return false
      }
    }

    return true
  }

  const handleSave = async () => {
    if (!originalSettings) {
      toast.error('Settings not loaded')
      return
    }

    if (selectedLanguages.length === 0) {
      toast.error('At least one language must be selected')
      return
    }

    if (!validateConfigurations()) {
      return
    }

    try {
      // Create enabled languages array from current configurations
      const enabledLanguages = selectedLanguages.map(languageCode => {
        const config = languageConfigs[languageCode]
        const existingLang = originalSettings.enabled_languages?.find(
          (lang: EnabledLanguage) => lang.language === languageCode
        )

        return {
          id: existingLang?.id,
          language: languageCode,
          web_address_enabled: config.web_address_enabled,
          web_address: config.web_address || '',
          google_analytics: config.google_analytics || '',
          google_tag_manager: config.google_tag_manager || '',
          facebook_pixel: config.facebook_pixel || '',
          facebook_page_id: config.facebook_page_id || '',
          hotjar: config.hotjar || '',
          tawk_to_widget_id: config.tawk_to_widget_id || '',
          enable_language_switcher: config.enable_language_switcher,
          price_multiplier: config.price_multiplier,
          shop_phone_number: config.shop_phone_number || '',
          default_language: config.default_language,
        }
      })

      // Ensure at least one language is marked as default
      const hasDefaultLanguage = enabledLanguages.some(
        lang => lang.default_language
      )
      if (!hasDefaultLanguage && enabledLanguages.length > 0) {
        enabledLanguages[0].default_language = true
      }

      await updateShopSettings({
        variables: {
          shopSettingsId: originalSettings.id,
          default_meta_image: originalSettings.default_meta_image?.src
            ? [originalSettings.default_meta_image?.src]
            : [],
          enable_vat: originalSettings.enable_vat,
          current_prices_with_vat: originalSettings.current_prices_with_vat,
          vat_percentage: originalSettings.vat_percentage,
          invoice_serial_number_letters:
            originalSettings.invoice_serial_number_letters,
          default_payment_method: originalSettings.default_payment_method?.id,
          default_shipping_method: originalSettings.default_shipping_method?.id,
          show_user_details_in_blog: originalSettings.show_user_details_in_blog,
          facebook: originalSettings.facebook || '',
          twitter: originalSettings.twitter || '',
          instagram: originalSettings.instagram || '',
          pinterest: originalSettings.pinterest || '',
          linkedin: originalSettings.linkedin || '',
          currency: selectedCurrency,
          automatic_product_translate:
            originalSettings.automatic_product_translate,
          product_position_script_on_save:
            originalSettings.product_position_script_on_save,
          automatic_image_generation:
            originalSettings.automatic_image_generation,
          enabled_languages: enabledLanguages,
          footer_logo_image: originalSettings.footer_logo_image?.src
            ? [originalSettings.footer_logo_image?.src]
            : [],
          footer_copyright_text: originalSettings.footer_copyright_text || '',
          revalidate_enabled: originalSettings.revalidate_enabled,
          revalidate_low_priority_links:
            originalSettings.revalidate_low_priority_links,
        },
      })

      toast.success('Language settings updated successfully')
      refetch()
    } catch (error) {
      console.error('Failed to update language settings:', error)
      toast.error('Failed to update language settings')
    }
  }

  const handleCancel = () => {
    if (data?.findFirstShopSetting) {
      const enabledLanguageCodes =
        data.findFirstShopSetting.enabled_languages?.map(
          (lang: EnabledLanguage) => lang.language
        ) || []
      setSelectedLanguages(enabledLanguageCodes)
      setSelectedCurrency(data.findFirstShopSetting.currency || 'EUR')
    }
  }

  if (loading) {
    return (
      <PageContent>
        <div className="flex justify-center items-center h-64">
          <div>Loading...</div>
        </div>
      </PageContent>
    )
  }

  return (
    <PageContent>
      <h1 className="text-2xl font-bold mb-8">Language Settings</h1>

      <div className="max-w-[1000px]">
        {/* Language Selection Section */}
        <div className="mb-8">
          <div className="grid grid-cols-12 gap-x-8">
            <div className="col-span-4">
              <h2 className="text-lg font-semibold">Select Languages</h2>
              <p className="text-sm text-gray-600 mt-1">
                Choose which languages should be available for your shop. At
                least one language must be selected.
              </p>
            </div>

            <div className="col-span-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {AVAILABLE_LANGUAGES.map(language => {
                  const isChecked = selectedLanguages.includes(language.code)

                  return (
                    <div
                      key={language.code}
                      className={`flex items-center space-x-3 p-3 rounded-lg border transition-colors ${
                        isChecked
                          ? 'bg-green-50 border-green-200'
                          : 'bg-white border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <Checkbox
                        checked={isChecked}
                        onCheckedChange={checked =>
                          handleLanguageToggle(
                            language.code,
                            checked as boolean
                          )
                        }
                        className={
                          isChecked ? 'bg-green-500 border-green-500' : ''
                        }
                      />
                      <div className="flex items-center space-x-2 flex-1">
                        <span className="text-2xl">{language.flag}</span>
                        <span className="font-medium text-sm">
                          {language.name}
                        </span>
                      </div>
                    </div>
                  )
                })}
              </div>

              {selectedLanguages.length === 0 && (
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    ⚠️ At least one language must be selected for your shop to
                    function properly.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="border-t my-8"></div>

        {/* Currency Section */}
        <div className="mb-8">
          <div className="grid grid-cols-12 gap-x-8">
            <div className="col-span-4">
              <h2 className="text-lg font-semibold">Currency</h2>
              <p className="text-sm text-gray-600 mt-1">
                Select the currency used throughout the shop.
              </p>
            </div>

            <div className="col-span-8">
              <div className="mb-6">
                <label className="block text-sm font-medium mb-1">
                  Currency
                </label>
                <Select
                  value={selectedCurrency}
                  onValueChange={setSelectedCurrency}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select currency" />
                  </SelectTrigger>
                  <SelectContent>
                    {AVAILABLE_CURRENCIES.map(currency => (
                      <SelectItem key={currency.code} value={currency.code}>
                        {currency.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>

        {/* Language Configuration Sections */}
        {selectedLanguages.length > 0 && (
          <>
            <div className="border-t my-8"></div>

            {selectedLanguages.map((langCode, index) => {
              const language = AVAILABLE_LANGUAGES.find(
                l => l.code === langCode
              )
              const config = languageConfigs[langCode]

              if (!language || !config) return null

              return (
                <div key={langCode}>
                  {index > 0 && <div className="border-t my-8"></div>}

                  <div className="mb-8">
                    <div className="grid grid-cols-12 gap-x-8">
                      <div className="col-span-4">
                        <div className="flex items-center space-x-3 mb-2">
                          <span className="text-2xl">{language.flag}</span>
                          <h2 className="text-lg font-semibold">
                            {language.name}
                          </h2>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          Configure domain and default language settings for{' '}
                          {language.name}.
                        </p>
                      </div>

                      <div className="col-span-8">
                        {/* Language Domain Toggle */}
                        <div className="mb-6">
                          <div className="flex items-center space-x-2">
                            <Switch
                              checked={config.web_address_enabled}
                              onCheckedChange={checked =>
                                updateLanguageConfig(langCode, {
                                  web_address_enabled: checked,
                                })
                              }
                            />
                            <label className="text-sm font-medium">
                              Language Domain
                            </label>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Does language have a domain? e.g. www.yourwebsite.lt
                          </p>
                        </div>

                        {/* Website Address Input - Only shown when domain is enabled */}
                        {config.web_address_enabled && (
                          <div className="mb-6">
                            <label className="block text-sm font-medium mb-1">
                              Website Address
                            </label>
                            <Input
                              value={config.web_address}
                              onChange={e =>
                                updateLanguageConfig(langCode, {
                                  web_address: e.target.value,
                                })
                              }
                              placeholder="https://test.shop.vilmers.furnisystems.com"
                              className="w-full"
                            />
                          </div>
                        )}

                        {/* Default Language Toggle - Only shown when domain is enabled */}
                        {config.web_address_enabled && (
                          <div className="mb-6">
                            <div className="flex items-center space-x-2">
                              <Switch
                                checked={config.default_language}
                                onCheckedChange={checked =>
                                  handleDefaultLanguageChange(langCode, checked)
                                }
                              />
                              <label className="text-sm font-medium">
                                Default Language
                              </label>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              Is this your default language?
                            </p>
                          </div>
                        )}

                        {/* Tawk.to Widget ID - Always shown */}
                        <div className="mb-6">
                          <label className="block text-sm font-medium mb-1">
                            Tawk.to Widget ID
                          </label>
                          <Input
                            value={config.tawk_to_widget_id || ''}
                            onChange={e =>
                              updateLanguageConfig(langCode, {
                                tawk_to_widget_id: e.target.value,
                              })
                            }
                            placeholder="e.g. 1a2b3c4d5e6f7g8h9i0j"
                            className="w-full"
                          />
                        </div>

                        {/* Analytics Settings - Only shown when domain is enabled */}
                        {config.web_address_enabled && (
                          <>
                            <div className="mb-6">
                              <label className="block text-sm font-medium mb-1">
                                Google Analytics
                              </label>
                              <Input
                                value={config.google_analytics || ''}
                                onChange={e =>
                                  updateLanguageConfig(langCode, {
                                    google_analytics: e.target.value,
                                  })
                                }
                                placeholder="e.g. G-XXXXXXXXXX or UA-XXXXXXXX-X"
                                className="w-full"
                              />
                            </div>

                            <div className="mb-6">
                              <label className="block text-sm font-medium mb-1">
                                Google Tag Manager
                              </label>
                              <Input
                                value={config.google_tag_manager || ''}
                                onChange={e =>
                                  updateLanguageConfig(langCode, {
                                    google_tag_manager: e.target.value,
                                  })
                                }
                                placeholder="e.g. GTM-XXXXXXX"
                                className="w-full"
                              />
                            </div>

                            <div className="mb-6">
                              <label className="block text-sm font-medium mb-1">
                                Facebook Pixel
                              </label>
                              <Input
                                value={config.facebook_pixel || ''}
                                onChange={e =>
                                  updateLanguageConfig(langCode, {
                                    facebook_pixel: e.target.value,
                                  })
                                }
                                placeholder="e.g. 123456789012345"
                                className="w-full"
                              />
                            </div>

                            <div className="mb-6">
                              <label className="block text-sm font-medium mb-1">
                                Facebook Chat
                              </label>
                              <Input
                                value={config.facebook_page_id || ''}
                                onChange={e =>
                                  updateLanguageConfig(langCode, {
                                    facebook_page_id: e.target.value,
                                  })
                                }
                                placeholder="e.g. 123456789012345"
                                className="w-full"
                              />
                            </div>

                            <div className="mb-6">
                              <label className="block text-sm font-medium mb-1">
                                Hotjar
                              </label>
                              <Input
                                value={config.hotjar || ''}
                                onChange={e =>
                                  updateLanguageConfig(langCode, {
                                    hotjar: e.target.value,
                                  })
                                }
                                placeholder="e.g. 1234567"
                                className="w-full"
                              />
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </>
        )}
      </div>

      <div className="max-w-[1000px] border-t mt-8 pt-6 flex justify-end items-center gap-4">
        <Button variant="outline" onClick={handleCancel} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={isSaving || selectedLanguages.length === 0}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </PageContent>
  )
}
